/* ==================================================================
   BAKOS — delivery estimate

   Renders a real "arrives by" date the way Amazon does, and keeps it
   honest: the date is computed in the SHOP's timezone, not the
   visitor's, so someone browsing from Los Angeles at 23:00 sees the
   same cutoff a customer in Bucharest does.

   Rules (all overridable from the theme editor):
     - orders placed before the cutoff dispatch the same working day
     - dispatch happens on working days only
     - delivery takes min..max working days after dispatch
     - Romanian public holidays are not working days

   Orthodox Easter moves every year, and three Romanian public
   holidays hang off it, so it is computed rather than hard-coded —
   a hard-coded list silently starts lying in January.
   ================================================================== */
(function () {
  'use strict';

  /* ---- date helpers, all operating on plain Y/M/D ---- */

  function ymd(y, m, d) { return { y: y, m: m, d: d }; }
  function key(o) { return o.y + '-' + String(o.m).padStart(2, '0') + '-' + String(o.d).padStart(2, '0'); }
  function toUTC(o) { return Date.UTC(o.y, o.m - 1, o.d); }
  function fromUTC(ms) { var t = new Date(ms); return ymd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()); }
  function addDays(o, n) { return fromUTC(toUTC(o) + n * 86400000); }
  function dow(o) { return new Date(toUTC(o)).getUTCDay(); } // 0 Sun .. 6 Sat

  /* Orthodox Easter (Meeus, Julian calendar) returned on the
     Gregorian calendar — the +13 day offset holds until 2100. */
  function orthodoxEaster(year) {
    var a = year % 4;
    var b = year % 7;
    var c = year % 19;
    var d = (19 * c + 15) % 30;
    var e = (2 * a + 4 * b - d + 34) % 7;
    var month = Math.floor((d + e + 114) / 31);
    var day = ((d + e + 114) % 31) + 1;
    return fromUTC(toUTC(ymd(year, month, day + 13)));
  }

  function romanianHolidays(year) {
    var easter = orthodoxEaster(year);
    var set = {};
    [
      ymd(year, 1, 1), ymd(year, 1, 2),        // New Year
      ymd(year, 1, 6), ymd(year, 1, 7),        // Epiphany, St John
      ymd(year, 1, 24),                        // Union Day
      addDays(easter, -2),                     // Good Friday
      easter,                                  // Easter Sunday
      addDays(easter, 1),                      // Easter Monday
      ymd(year, 5, 1),                         // Labour Day
      ymd(year, 6, 1),                         // Children's Day
      addDays(easter, 49),                     // Pentecost
      addDays(easter, 50),                     // Whit Monday
      ymd(year, 8, 15),                        // Assumption
      ymd(year, 11, 30),                       // St Andrew
      ymd(year, 12, 1),                        // National Day
      ymd(year, 12, 25), ymd(year, 12, 26)     // Christmas
    ].forEach(function (o) { set[key(o)] = true; });
    return set;
  }

  var holidayCache = {};
  function isHoliday(o, extra) {
    if (extra && extra[key(o)]) return true;
    if (!holidayCache[o.y]) holidayCache[o.y] = romanianHolidays(o.y);
    return !!holidayCache[o.y][key(o)];
  }

  function isWorkingDay(o, workingDays, extra) {
    return workingDays.indexOf(dow(o)) !== -1 && !isHoliday(o, extra);
  }

  function nextWorkingDay(o, workingDays, extra) {
    var guard = 0;
    var cur = o;
    while (!isWorkingDay(cur, workingDays, extra)) {
      cur = addDays(cur, 1);
      if (++guard > 40) return cur; // never spin forever on bad settings
    }
    return cur;
  }

  function addWorkingDays(o, n, workingDays, extra) {
    var cur = o;
    var added = 0;
    var guard = 0;
    while (added < n) {
      cur = addDays(cur, 1);
      if (isWorkingDay(cur, workingDays, extra)) added++;
      if (++guard > 200) break;
    }
    return cur;
  }

  /* ---- what time is it, where the shop is ---- */
  function shopNow(timezone) {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: timezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
    } catch (err) {
      // an invalid timezone must not take the widget down
      var l = new Date();
      return { date: ymd(l.getFullYear(), l.getMonth() + 1, l.getDate()), hour: l.getHours(), minute: l.getMinutes() };
    }
    var get = function (t) { return parseInt((parts.find(function (p) { return p.type === t; }) || {}).value, 10); };
    return { date: ymd(get('year'), get('month'), get('day')), hour: get('hour'), minute: get('minute') };
  }

  /* ---- formatting ---- */
  function formatDate(o, locale, style) {
    var dt = new Date(toUTC(o));
    var opts = style === 'long'
      ? { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
      : { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' };
    try { return new Intl.DateTimeFormat(locale, opts).format(dt); }
    catch (err) { return new Intl.DateTimeFormat('en-GB', opts).format(dt); }
  }

  /* ================================================================
     <bakos-delivery> custom element
     ================================================================ */
  class BakosDelivery extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;
      this.render();
      // recompute on the minute so the countdown ticks and the date
      // rolls over on its own if the page is left open past midnight
      this._timer = setInterval(this.render.bind(this), 30000);
      // a page restored from bfcache can be hours stale
      window.addEventListener('pageshow', this.render.bind(this));
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) this.render();
      }.bind(this));
    }

    disconnectedCallback() { clearInterval(this._timer); }

    get config() {
      var d = this.dataset;
      var working = (d.workingDays || '1,2,3,4,5').split(',')
        .map(function (n) { return parseInt(n, 10); })
        .filter(function (n) { return n >= 0 && n <= 6; });
      if (!working.length) working = [1, 2, 3, 4, 5];

      var extra = {};
      (d.extraHolidays || '').split(',').forEach(function (s) {
        s = s.trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) extra[s] = true;
      });

      return {
        timezone: d.timezone || 'Europe/Bucharest',
        locale: d.locale || 'en-GB',
        cutoffHour: parseInt(d.cutoffHour, 10),
        cutoffMinute: parseInt(d.cutoffMinute, 10) || 0,
        minDays: Math.max(1, parseInt(d.minDays, 10) || 1),
        maxDays: Math.max(1, parseInt(d.maxDays, 10) || 2),
        workingDays: working,
        extraHolidays: extra
      };
    }

    compute() {
      var c = this.config;
      var now = shopNow(c.timezone);

      var cutoffHour = isFinite(c.cutoffHour) ? c.cutoffHour : 14;
      var pastCutoff = (now.hour > cutoffHour) ||
                       (now.hour === cutoffHour && now.minute >= c.cutoffMinute);

      // dispatch day: today if today is a working day and we are in
      // time, otherwise the next working day
      var dispatch;
      if (isWorkingDay(now.date, c.workingDays, c.extraHolidays) && !pastCutoff) {
        dispatch = now.date;
        var minsLeft = (cutoffHour * 60 + c.cutoffMinute) - (now.hour * 60 + now.minute);
        this._countdown = { hours: Math.floor(minsLeft / 60), minutes: minsLeft % 60 };
      } else {
        dispatch = nextWorkingDay(addDays(now.date, 1), c.workingDays, c.extraHolidays);
        this._countdown = null;
      }

      return {
        from: addWorkingDays(dispatch, c.minDays, c.workingDays, c.extraHolidays),
        to: addWorkingDays(dispatch, c.maxDays, c.workingDays, c.extraHolidays),
        dispatch: dispatch,
        locale: c.locale
      };
    }

    render() {
      var r;
      try { r = this.compute(); } catch (err) { this.hidden = true; return; }

      var dateEl = this.querySelector('[data-bakos-delivery-date]');
      var cutEl = this.querySelector('[data-bakos-delivery-cutoff]');
      if (!dateEl) return;

      var sameDay = key(r.from) === key(r.to);
      var text = sameDay
        ? formatDate(r.from, r.locale, 'long')
        : formatDate(r.from, r.locale, 'short') + ' – ' + formatDate(r.to, r.locale, 'short');

      dateEl.textContent = text;
      this.setAttribute('datetime', key(r.from));

      if (cutEl) {
        if (this._countdown && (this._countdown.hours > 0 || this._countdown.minutes > 0)) {
          var tpl = this.dataset.cutoffText || 'Order within [time]';
          var t = this._countdown.hours > 0
            ? this._countdown.hours + 'h ' + this._countdown.minutes + 'm'
            : this._countdown.minutes + 'm';
          cutEl.textContent = tpl.replace('[time]', t);
          cutEl.hidden = false;
        } else {
          cutEl.hidden = true;
        }
      }

      this.hidden = false;
    }
  }

  if (!customElements.get('bakos-delivery')) {
    customElements.define('bakos-delivery', BakosDelivery);
  }
})();
